import * as React from "react";
import { MobxComponent } from "../../../../mobx/components/index";
import { withTranslation, WithTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import { FaExclamationCircle, FaTrash, FaUser } from 'react-icons/fa';
import throttle from "lodash/throttle";
import { RotateLoader } from "@lib/components";
import { Header, List, Loader, Icon, Item, ItemContent, Stripe, ItemLoader } from "./list.components";
import moment from "moment-timezone";
import { renderNotificationBody, renderNotificationTitle } from "./renderNotificationBody";

interface Props extends WithTranslation { }
interface State { }

const event_colors = {
	age_verification: "#45709c",
	order_new: "#489b37",
	fraud_risk_order_new: '#e60000',
	uber_order_cancel: '#e60000',
	order_deleted: '#e60000',
	booking_deleted: '#e60000',
	booking_cancelled: '#e60000',
};

@inject("store") @observer
class NotificationsListClass extends MobxComponent<Props, State> {

	list: React.RefObject<HTMLDivElement> = React.createRef();
	lastTouchAt: number = 0;
	touchStartX: number = 0;
	touchStartY: number = 0;
	touchListScrollTop: number = 0;

	handleScroll = () => {
		const el = document.getElementById("notification-list");
		if (!el) return;
		const distanceFromBottom = (el.scrollHeight - el.scrollTop) - el.clientHeight;
		if (distanceFromBottom < 40) {
			this.injected.store.notifications.on_scroll_bottom();
		}
	}

	handleItemPress = (item: T.Lib.GetStream.NotificationItem, i: number) => {
		const { notifications } = this.injected.store;
		notifications.handle_click(item, i);
	}

	isTouchTap = (e: React.TouchEvent) => {
		const touch = e.changedTouches[0];
		if (!touch) return false;

		const dx = Math.abs(touch.clientX - this.touchStartX);
		const dy = Math.abs(touch.clientY - this.touchStartY);
		if (dx > 10 || dy > 10) {
			return false;
		}

		const list = document.getElementById("notification-list");
		if (list && Math.abs(list.scrollTop - this.touchListScrollTop) > 0) {
			return false;
		}

		return true;
	}

	render() {
		const { t } = this.injected;
		const { notifications } = this.injected.store;
		const { items, loading, all_items_queried, unread } = notifications.s;
		return (
			<div>
				<Header>
					<h4>Notifications</h4>
					<p className="small font-bold">{unread} unread</p>
				</Header>
				{(items.length === 0 && loading) && (
					<Loader>
						<RotateLoader size={3} />
					</Loader>
				)}
				{(items.length === 0 && !loading) && (
					<Loader>
						<h4 className="m-b-2">No notifications yet!</h4>
						<p className="lhp">The notifications panel will keep you up to date on the latest events such as new orders, bookings and customer requests</p>
					</Loader>
				)}
				{(items.length > 0) && (
					<List id="notification-list" onScroll={throttle(this.handleScroll, 200)}>
						{items.map((item, i) => {
							const { time, verb, is_read } = item;
							const date = moment.utc(time, moment.ISO_8601);
							return (
								<Item
									key={i}
									onTouchStart={(e) => {
										e.stopPropagation();
										const touch = e.touches[0];
										if (touch) {
											this.touchStartX = touch.clientX;
											this.touchStartY = touch.clientY;
											const list = document.getElementById("notification-list");
											this.touchListScrollTop = list?.scrollTop ?? 0;
										}
										this.lastTouchAt = Date.now();
									}}
									onTouchEnd={(e) => {
										e.stopPropagation();
										if (!this.isTouchTap(e)) {
											return;
										}
										e.preventDefault();
										this.handleItemPress(item, i);
									}}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										// Skip synthetic click right after touch to prevent duplicate handling.
										if (Date.now() - this.lastTouchAt < 600) {
											return;
										}
										this.handleItemPress(item, i);
									}}>
									<Stripe style={{ background: is_read ? "transparent" : event_colors[verb as keyof typeof event_colors] }} />
									<ItemContent>
										<div className="flex-line centered">
											<Icon>
												{verb === "fraud_risk_order_new" ? <FaExclamationCircle color="red"/> : (verb === "order_deleted" || verb === "booking_deleted") ? <FaTrash color="red"/> : <FaUser />}
											</Icon>
											<div>
												<p className="lhp font-semi-bold">{renderNotificationTitle(item)}</p>
												<p className="lhp">{renderNotificationBody(item)}</p>
												{(date.isValid() && typeof date.valueOf() === "number") && (
													<p className="small italic m-t-1 notranslate">
														{t("relative", { value: date.valueOf() })}
													</p>
												)}
											</div>
										</div>
									</ItemContent>
								</Item>
							);
						})}
						{loading && (
							<ItemLoader>
								<RotateLoader size={2} />
							</ItemLoader>
						)}
						{all_items_queried && (
							<ItemLoader>
								<p className="italic">No more notifications...</p>
							</ItemLoader>
						)}
					</List>
				)}
			</div>
		);
	}

}

export const NotificationsList = withTranslation()(NotificationsListClass);
